import type { CollaboratorCustomField } from "@services/CollaboratorCustomFieldsService";

// Helpers to show, edit and validate custom field values (same rules as the
// collaborator profile in ColaboradorInfo). Stored values are strings:
// dates "YYYY-MM-DD", booleans "true"/"false", list fields the option id.

export const sanitizeDateOnlyDisplayInput = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 8);
    const day = digits.slice(0, 2);
    const month = digits.slice(2, 4);
    const year = digits.slice(4, 8);
    return [day, month, year].filter(Boolean).join("/");
};

export const parseDateOnlyFromDisplay = (value: string): string => {
    if (!value) {
        return "";
    }
    const [day, month, year] = value.split("/");
    return `${year}-${month}-${day}`;
};

export const formatDateOnlyForDisplay = (value?: string): string => {
    if (!value) {
        return "";
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
};

export const isValidDateOnlyDisplay = (value: string): boolean => {
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        return false;
    }
    const [day, month, year] = value.split("/").map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

export const sanitizeNumberInput = (value: string): string =>
    value.replace(/[^0-9.,-]/g, "").replace(",", ".");

/** Sanitizes what the user types according to the field type */
export const sanitizeCustomFieldInput = (field: CollaboratorCustomField, value: string): string => {
    if (field.typeId === "number") {
        return sanitizeNumberInput(value);
    }
    if (field.typeId === "date") {
        return sanitizeDateOnlyDisplayInput(value);
    }
    return value;
};

/** Stored values → form values (dates shown as DD/MM/AAAA) */
export const buildCustomFieldFormValues = (
    fields: CollaboratorCustomField[],
    storedValues: Record<string, string>,
): Record<string, string> => {
    return fields.reduce<Record<string, string>>((formValues, field) => {
        const value = storedValues[field.id] ?? "";
        formValues[field.id] = field.typeId === "date" ? formatDateOnlyForDisplay(value) : value;
        return formValues;
    }, {});
};

/** Form value → stored value (dates back to YYYY-MM-DD) */
export const toStoredCustomFieldValue = (field: CollaboratorCustomField | undefined, value: string): string =>
    field?.typeId === "date" ? parseDateOnlyFromDisplay(value) : value;

/**
 * Only the custom fields whose value changed, already in stored format.
 * Spaces alone don't count as a change.
 */
export const getChangedCustomFieldValues = (
    fields: CollaboratorCustomField[],
    initialFormValues: Record<string, string>,
    formValues: Record<string, string>,
): Record<string, string> => {
    return fields.reduce<Record<string, string>>((changed, field) => {
        const value = formValues[field.id] ?? "";
        if (value.trim() !== (initialFormValues[field.id] ?? "").trim()) {
            changed[field.id] = toStoredCustomFieldValue(field, value);
        }
        return changed;
    }, {});
};

export const validateCustomFieldValues = (
    fields: CollaboratorCustomField[],
    formValues: Record<string, string>,
): Record<string, string> => {
    return fields.reduce<Record<string, string>>((errors, field) => {
        const value = formValues[field.id] ?? "";
        if (field.isRequired && field.typeId !== "boolean" && !value.trim()) {
            errors[field.id] = "Campo obligatorio".localized();
            return errors;
        }
        if (field.typeId === "number" && value && Number.isNaN(Number(value))) {
            errors[field.id] = "Ingresá un número válido".localized();
        }
        if (field.typeId === "date" && value && !isValidDateOnlyDisplay(value)) {
            errors[field.id] = "Ingresá una fecha válida".localized();
        }
        return errors;
    }, {});
};

/** Value as the user should read it (read-only views) */
export const getCustomFieldDisplayValue = (field: CollaboratorCustomField, storedValue?: string): string => {
    const value = storedValue ?? "";
    if (field.typeId === "boolean") {
        if (!value) {
            return "";
        }
        return value === "true" ? "Sí".localized() : "No".localized();
    }
    if (field.typeId === "list") {
        return field.values?.find((listValue) => listValue.id === value)?.label ?? value;
    }
    if (field.typeId === "date") {
        return formatDateOnlyForDisplay(value);
    }
    return value;
};
